import { Component, Inject, OnInit, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { HttpClient } from '@angular/common/http';
import { authBackendEndPointToken, specialtiesToken } from '../app.config';

interface PractitionerSpecialty {
  code: string;
  name: string;
}

interface PermissionCategory {
  code: string;
  display: string;
  system?: string;
  isManual?: boolean; // Flag to identify manually added categories
}

interface RoleConfiguration {
  roleName: string;
  specialty?: string;
  hasFullAccess: boolean;
  permissions: {
    allowedServiceRequestCategories: string[];
    allowedObservationCategories: string[];
    customPermissions: {
      canViewAllPatients: boolean;
      canOrderTests: boolean;
      canPrescribeMedications: boolean;
      canPerformProcedures: boolean;
    };
  };
}

@Component({
  selector: 'app-role-selection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatRadioModule,
    MatExpansionModule,
    MatAutocompleteModule,
    FormsModule,
    ReactiveFormsModule
  ],
  template: `
    <h2 mat-dialog-title>
      <div class="g-just-flex gap-8" style="align-items: center;">
        <mat-icon>manage_accounts</mat-icon>
        <span>{{ data.title || 'Configure Roles & Permissions' }}</span>
      </div>
    </h2>

    <mat-dialog-content>
      <div class="dialog-content">
        @if(data.message) {
          <p class="message-text" style="margin-bottom: 20px; color: var(--mat-sys-on-surface-variant);">
            {{ data.message }}
          </p>
        }

        <div class="roles-list">
          @for(role of data.availableRoles; track role.code) {
            <mat-expansion-panel
              class="role-panel"
              [expanded]="selectedRoles[role.code]"
              (opened)="onRoleExpanded(role.code)"
              (closed)="onRoleClosed(role.code)">

              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-checkbox
                    [(ngModel)]="selectedRoles[role.code]"
                    (click)="$event.stopPropagation()"
                    (change)="onRoleCheckboxChange(role.code, $event.checked)"
                    color="primary">
                    <div class="g-just-flex gap-8" style="align-items: center;">
                      <span style="font-weight: 500;">{{ role.display }}</span>
                      @if(requiresSpecialty(role.code) && roleConfigs[role.code]?.specialty) {
                        <span class="specialty-badge">
                          {{ roleConfigs[role.code].specialty }}
                        </span>
                      }
                    </div>
                  </mat-checkbox>
                </mat-panel-title>
              </mat-expansion-panel-header>

              @if(selectedRoles[role.code]) {
                <div class="role-config-content">

                  <!-- Show configuration only for Doctor/Nurse/ServiceProvider -->
                  @if(requiresConfiguration(role.code)) {
                    <!-- Specialty Field (for all configurable roles) with Autocomplete -->
                    @if(requiresSpecialty(role.code)) {
                      <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Specialty *</mat-label>
                        <input
                          matInput
                          [(ngModel)]="roleConfigs[role.code].specialty"
                          [matAutocomplete]="auto"
                          placeholder="e.g., Cardiology, Radiology, Orthopedics"
                          (input)="onSpecialtyInput($event, role.code)"
                          required>
                        <mat-icon matPrefix>medical_services</mat-icon>
                        <mat-hint>Start typing or select from suggestions</mat-hint>
                        <mat-autocomplete #auto="matAutocomplete" [displayWith]="displaySpecialty">
                          @for(specialty of filteredSpecialties[role.code]; track specialty.code) {
                            <mat-option [value]="specialty.name">
                              {{ specialty.name }}
                            </mat-option>
                          }
                        </mat-autocomplete>
                      </mat-form-field>
                    }

                    <!-- Doctor and Nurse get full access automatically -->
                    @if(isDoctorOrNurse(role.code)) {
                      <div class="info-box" style="padding: 12px; background: #e8f5e9; border-left: 4px solid #4caf50; border-radius: 4px;">
                        <div style="display: flex; align-items: center; gap: 8px; font-weight: 600; color: #2e7d32; margin-bottom: 4px;">
                          <mat-icon style="font-size: 18px;">verified</mat-icon>
                          Full Access Granted
                        </div>
                        <div style="font-size: 0.85rem; color: #388e3c; line-height: 1.4;">
                          {{ role.display }}s automatically have unrestricted access to all service requests, observations, and patient data.
                        </div>
                      </div>
                    }

                    <!-- Service Provider gets custom access level options -->
                    @if(isServiceProvider(role.code)) {
                      <!-- Access Level -->
                      <div class="access-level-section">
                        <h4 style="margin: 16px 0 8px 0; font-size: 0.95rem; color: var(--mat-sys-on-surface-variant);">
                          Access Level
                        </h4>
                        <mat-radio-group
                          [(ngModel)]="roleConfigs[role.code].hasFullAccess"
                          (change)="onAccessLevelChange(role.code)">
                          <mat-radio-button [value]="true" style="margin-bottom: 8px;">
                            <strong>✨ Full Access</strong>
                            <span style="display: block; font-size: 0.85rem; color: var(--mat-sys-on-surface-variant); margin-top: 2px;">
                              Unrestricted access to all features and patient data
                            </span>
                          </mat-radio-button>
                          <mat-radio-button [value]="false">
                            <strong>🎯 Custom Permissions</strong>
                            <span style="display: block; font-size: 0.85rem; color: var(--mat-sys-on-surface-variant); margin-top: 2px;">
                              Specialty-specific access control
                            </span>
                          </mat-radio-button>
                        </mat-radio-group>
                      </div>

                      <!-- Warning for Full Access -->
                      @if(roleConfigs[role.code].hasFullAccess) {
                        <div class="warning-box" style="margin-top: 16px; padding: 12px; background: #fff3e0; border-left: 4px solid #ff9800; border-radius: 4px;">
                          <div style="display: flex; align-items: center; gap: 8px; font-weight: 600; color: #e65100; margin-bottom: 4px;">
                            <mat-icon style="font-size: 18px;">warning</mat-icon>
                            Full Access Granted
                          </div>
                          <div style="font-size: 0.85rem; color: #f57c00; line-height: 1.4;">
                            This service provider will have unrestricted access to all service requests, observations, and patient data across all categories.
                          </div>
                        </div>
                      }

                      <!-- Granular Permissions (only if Limited Access for ServiceProvider) -->
                      @if(!roleConfigs[role.code].hasFullAccess) {
                        <div class="permissions-section">

                          <!-- Category Filtering Warning -->
                          <div class="info-box" style="padding: 12px; background: #e3f2fd; border-left: 4px solid #2196f3; border-radius: 4px; margin-bottom: 16px;">
                            <div style="display: flex; align-items: center; gap: 8px; font-weight: 600; color: #1565c0; margin-bottom: 4px;">
                              <mat-icon style="font-size: 18px;">info</mat-icon>
                              Specialty-Based Filtering
                            </div>
                            <div style="font-size: 0.85rem; color: #1976d2; line-height: 1.4;">
                              This {{ role.display.toLowerCase() }} will ONLY see service requests and observations in the selected categories.
                              They keep all {{ role.display.toLowerCase() }} permissions (approve, cancel, view), but ONLY for items in these categories.
                            </div>
                          </div>

                          <!-- Service Request Categories -->
                          <div class="permission-group">
                            <h4 style="margin: 16px 0 8px 0; font-size: 0.95rem; color: var(--mat-sys-on-surface-variant); display: flex; align-items: center; gap: 8px;">
                              <mat-icon style="font-size: 18px;">assignment</mat-icon>
                              Allowed Service Request Categories
                            </h4>
                            @if(loadingServiceCategories) {
                              <p style="font-size: 0.9rem; color: var(--mat-sys-on-surface-variant);">Loading...</p>
                            } @else if(serviceRequestCategories.length > 0) {
                              <div class="categories-grid">
                                @for(category of serviceRequestCategories; track category.code) {
                                  <mat-checkbox
                                    [(ngModel)]="serviceRequestSelection[role.code][category.code]"
                                    (change)="updateServiceRequestCategories(role.code)"
                                    color="primary"
                                    class="category-checkbox">
                                    <span>{{ category.display }}</span>
                                  </mat-checkbox>
                                }
                              </div>
                            } @else {
                              <p style="font-size: 0.9rem; color: var(--mat-sys-error);">Failed to load categories</p>
                            }
                          </div>

                          <!-- Observation Categories -->
                          <div class="permission-group">
                            <h4 style="margin: 16px 0 8px 0; font-size: 0.95rem; color: var(--mat-sys-on-surface-variant); display: flex; align-items: center; gap: 8px;">
                              <mat-icon style="font-size: 18px;">monitor_heart</mat-icon>
                              Allowed Observation Categories
                            </h4>
                            @if(loadingObsCategories) {
                              <p style="font-size: 0.9rem; color: var(--mat-sys-on-surface-variant);">Loading...</p>
                            } @else if(observationCategories.length > 0) {
                              <div class="categories-grid">
                                @for(category of observationCategories; track category.code) {
                                  <mat-checkbox
                                    [(ngModel)]="observationSelection[role.code][category.code]"
                                    (change)="updateObservationCategories(role.code)"
                                    color="primary"
                                    class="category-checkbox">
                                    <span>{{ category.display }}</span>
                                  </mat-checkbox>
                                }
                              </div>
                            } @else {
                              <p style="font-size: 0.9rem; color: var(--mat-sys-error);">Failed to load categories</p>
                            }
                          </div>

                          <!-- Additional Capabilities -->
                          <div class="permission-group">
                            <h4 style="margin: 16px 0 8px 0; font-size: 0.95rem; color: var(--mat-sys-on-surface-variant); display: flex; align-items: center; gap: 8px;">
                              <mat-icon style="font-size: 18px;">settings</mat-icon>
                              Additional Capabilities
                            </h4>
                            <div class="categories-grid">
                              <mat-checkbox
                                [(ngModel)]="roleConfigs[role.code].permissions.customPermissions.canOrderTests"
                                color="primary"
                                class="category-checkbox">
                                Can Order Tests
                              </mat-checkbox>
                              <mat-checkbox
                                [(ngModel)]="roleConfigs[role.code].permissions.customPermissions.canPrescribeMedications"
                                color="primary"
                                class="category-checkbox">
                                Can Prescribe Medications
                              </mat-checkbox>
                              <mat-checkbox
                                [(ngModel)]="roleConfigs[role.code].permissions.customPermissions.canPerformProcedures"
                                color="primary"
                                class="category-checkbox">
                                Can Perform Procedures
                              </mat-checkbox>
                            </div>
                          </div>
                        </div>
                      }
                    }
                  } @else {
                    <!-- Simple confirmation for other roles -->
                    <div style="padding: 16px; background: #e8f5e9; border-left: 4px solid #4caf50; border-radius: 4px;">
                      <div style="display: flex; align-items: center; gap: 8px; font-weight: 600; color: #2e7d32; margin-bottom: 4px;">
                        <mat-icon style="font-size: 18px;">check_circle</mat-icon>
                        Role Selected
                      </div>
                      <div style="font-size: 0.85rem; color: #388e3c; line-height: 1.4;">
                        This role will be assigned with default permissions.
                      </div>
                    </div>
                  }
                </div>
              }
            </mat-expansion-panel>
          }
        </div>

        @if(getSelectedCount() > 0) {
          <div class="selected-summary" style="margin-top: 20px; padding: 12px; background-color: var(--mat-sys-primary-container); border-radius: 8px; color: var(--mat-sys-on-primary-container);">
            <div style="display: flex; align-items: center; gap: 8px;">
              <mat-icon>check_circle</mat-icon>
              <strong>{{ getSelectedCount() }}</strong> role(s) configured
            </div>
            @if(hasValidationErrors()) {
              <div style="color: var(--mat-sys-error); margin-top: 8px; display: flex; align-items: center; gap: 8px;">
                <mat-icon style="font-size: 18px;">error</mat-icon>
                Please complete all required fields
              </div>
            }
          </div>
        }
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button
        mat-flat-button
        color="primary"
        (click)="onSave()"
        [disabled]="!canSave()">
        <mat-icon>check</mat-icon>
        Save and Approve
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-content {
      min-width: 500px;
      max-width: 700px;
      padding: 8px 0;
    }

    .message-text {
      font-size: 0.95rem;
      line-height: 1.5;
    }

    .roles-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .role-panel {
      margin-bottom: 8px;
    }

    .role-config-content {
      padding: 16px 0;
    }

    .full-width {
      width: 100%;
    }

    .specialty-badge {
      padding: 2px 8px;
      background: #ff9800;
      color: white;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
    }

    .access-level-section mat-radio-button {
      display: block;
      margin-bottom: 12px;
    }

    .permissions-section {
      margin-top: 16px;
      padding: 16px;
      background-color: var(--mat-sys-surface-variant);
      border-radius: 8px;
    }

    .permission-group {
      margin-bottom: 20px;
    }

    .permission-group:last-child {
      margin-bottom: 0;
    }

    .categories-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 8px;
      margin-top: 8px;
    }

    .category-checkbox {
      display: block;
      padding: 4px;
    }

    mat-dialog-actions {
      padding: 16px 24px;
      gap: 8px;
    }

    .g-just-flex {
      display: flex;
      justify-content: flex-start;
    }

    .gap-8 {
      gap: 8px;
    }

    ::ng-deep .mat-expansion-panel-header-title {
      flex-grow: 1;
    }
  `]
})
export class RoleSelectionDialogComponent implements OnInit {
  private http = inject(HttpClient);
  backendApi = inject(authBackendEndPointToken);
  specialtiesTokenValue = inject(specialtiesToken);

  selectedRoles: { [key: string]: boolean } = {};
  roleConfigs: { [key: string]: RoleConfiguration } = {};
  validCodes = ['laboratory', 'imaging', 'procedure', 'surgery', 'others'];

  serviceRequestCategories: PermissionCategory[] = [];
  observationCategories: PermissionCategory[] = [];

  loadingServiceCategories = false;
  loadingObsCategories = false;

  serviceRequestSelection: { [roleCode: string]: { [categoryCode: string]: boolean } } = {};
  observationSelection: { [roleCode: string]: { [categoryCode: string]: boolean } } = {};

  practitionerSpecialties: PractitionerSpecialty[] = [];
  filteredSpecialties: { [roleCode: string]: PractitionerSpecialty[] } = {};

  constructor(
    public dialogRef: MatDialogRef<RoleSelectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      availableRoles: { code: string; display: string }[];
      selectedRoles: { [key: string]: boolean };
      existingConfigurations?: any[];
      title?: string;
      message?: string;
    }
  ) {
    this.selectedRoles = { ...data.selectedRoles };
    this.initializeRoleConfigs();
  }

  async ngOnInit(): Promise<void> {
    this.loadSpecialties();
    await this.loadCategories();
    if (this.data.existingConfigurations && this.data.existingConfigurations.length > 0) {
      this.loadExistingConfigurations();
    }
  }

  private filterInvalidServiceCategories(categories: string[]): string[] {
    const validCodes = this.serviceRequestCategories.map(c => c.code);
    const filtered = categories.filter(cat => validCodes.includes(cat));

    const dropped = categories.length - filtered.length;
    if (dropped > 0) {
      const droppedItems = categories.filter(cat => !validCodes.includes(cat));
      console.warn(`⚠️ Dropped ${dropped} invalid service request categories:`, droppedItems);
      console.warn('ℹ️ Valid service codes are:', validCodes);
    }

    return filtered;
  }

  private loadExistingConfigurations(): void {
    console.log('📥 Loading existing configurations:', this.data.existingConfigurations);

    this.data.existingConfigurations?.forEach(config => {
      const roleCode = config.roleName.toLowerCase();

      // Mark role as selected
      this.selectedRoles[roleCode] = true;

      // ✅ Filter service request categories to only include valid ones
      const filteredServiceCategories = this.filterInvalidServiceCategories(
        config.permissions?.allowedServiceRequestCategories || []
      );

      console.log(`🔍 Filtering for ${roleCode}:`, {
        original: config.permissions?.allowedServiceRequestCategories,
        filtered: filteredServiceCategories
      });

      // Load the full configuration
      this.roleConfigs[roleCode] = {
        roleName: roleCode,
        specialty: config.specialty,
        hasFullAccess: config.hasFullAccess,
        permissions: {
          allowedServiceRequestCategories: filteredServiceCategories,
          allowedObservationCategories: [...(config.permissions?.allowedObservationCategories || [])],
          customPermissions: {
            canViewAllPatients: config.permissions?.customPermissions?.canViewAllPatients || false,
            canOrderTests: config.permissions?.customPermissions?.canOrderTests || false,
            canPrescribeMedications: config.permissions?.customPermissions?.canPrescribeMedications || false,
            canPerformProcedures: config.permissions?.customPermissions?.canPerformProcedures || false
          }
        }
      };

      // ✅ Only set checkboxes for FILTERED categories (only for ServiceProvider)
      if (this.isServiceProvider(roleCode)) {
        this.serviceRequestSelection[roleCode] = {};
        filteredServiceCategories.forEach((categoryCode: string) => {
          if (this.serviceRequestCategories.some(c => c.code.toLowerCase() === categoryCode.toLowerCase())) {
            this.serviceRequestSelection[roleCode][categoryCode] = true;
          } else {
            console.warn(`⚠️ Category ${categoryCode} not found in available categories, skipping checkbox`);
          }
        });

        this.observationSelection[roleCode] = {};
        config.permissions?.allowedObservationCategories?.forEach((categoryCode: string) => {
          if (this.observationCategories.some(c => c.code.toLowerCase() === categoryCode.toLowerCase())) {
            this.observationSelection[roleCode][categoryCode] = true;
          }
        });
      }

      console.log(`✅ Loaded existing config for ${roleCode}:`, {
        hasFullAccess: config.hasFullAccess,
        serviceCategories: filteredServiceCategories.length,
        serviceCategoriesChecked: Object.keys(this.serviceRequestSelection[roleCode] || {}).length,
        obsCategories: config.permissions?.allowedObservationCategories?.length || 0,
        obsCategoriesChecked: Object.keys(this.observationSelection[roleCode] || {}).length
      });
    });
  }

  private loadSpecialties(): void {
    this.practitionerSpecialties = this.specialtiesTokenValue.map(s => ({
      code: s.code,
      name: s.display
    }));

    this.data.availableRoles.forEach(role => {
      this.filteredSpecialties[role.code] = [...this.practitionerSpecialties];
    });
  }

  private initializeRoleConfigs(): void {
    this.data.availableRoles.forEach(role => {
      // Doctor and Nurse always have full access
      const isFullAccess = this.isDoctorOrNurse(role.code);

      this.roleConfigs[role.code] = {
        roleName: role.code,
        specialty: undefined,
        hasFullAccess: isFullAccess,
        permissions: {
          allowedServiceRequestCategories: [],
          allowedObservationCategories: [],
          customPermissions: {
            canViewAllPatients: false,
            canOrderTests: false,
            canPrescribeMedications: false,
            canPerformProcedures: false
          }
        }
      };
      this.serviceRequestSelection[role.code] = {};
      this.observationSelection[role.code] = {};
      this.filteredSpecialties[role.code] = [];
    });
  }

  /**
   * Load categories from API
   */
  private async loadCategories(): Promise<void> {
    // Load Service Request Categories
    this.loadingServiceCategories = true;
    try {
      const fetchedServiceCategories = await this.http
        .get<PermissionCategory[]>(`${this.backendApi}/api/Categories/service-request-categories`)
        .toPromise() || [];

      this.serviceRequestCategories = fetchedServiceCategories;

      console.log('✅ Service Request Categories loaded:', this.serviceRequestCategories.length);
    } catch (error) {
      console.error('Failed to load service request categories:', error);
      this.serviceRequestCategories = [];
    } finally {
      this.loadingServiceCategories = false;
    }

    // Load Observation Categories
    this.loadingObsCategories = true;
    try {
      const fetchedObsCategories = await this.http
        .get<PermissionCategory[]>(`${this.backendApi}/api/Categories/observation-categories`)
        .toPromise() || [];

      this.observationCategories = fetchedObsCategories;

      console.log('✅ Observation Categories loaded:', this.observationCategories.length);
    } catch (error) {
      console.error('Failed to load observation categories:', error);
    } finally {
      this.loadingObsCategories = false;
    }
  }

  requiresSpecialty(roleCode: string): boolean {
    const lowerRole = roleCode.toLowerCase();
    return lowerRole === 'doctor' || lowerRole === 'nurse' || lowerRole === 'serviceprovider';
  }

  requiresConfiguration(roleCode: string): boolean {
    const lowerRole = roleCode.toLowerCase();
    return lowerRole === 'doctor' || lowerRole === 'nurse' || lowerRole === 'serviceprovider';
  }

  isDoctorOrNurse(roleCode: string): boolean {
    const lowerRole = roleCode.toLowerCase();
    return lowerRole === 'doctor' || lowerRole === 'nurse';
  }

  isServiceProvider(roleCode: string): boolean {
    const lowerRole = roleCode.toLowerCase();
    return lowerRole === 'serviceprovider';
  }

  onRoleExpanded(roleCode: string): void {
    this.selectedRoles[roleCode] = true;

    // Doctors and Nurses always get full access - no defaults needed
    if (this.isDoctorOrNurse(roleCode)) {
      this.roleConfigs[roleCode].hasFullAccess = true;
      return;
    }

    // For ServiceProvider, only set defaults if this is a NEW role (not being edited)
    const hasExistingConfig = this.data.existingConfigurations?.some(
      config => config.roleName.toLowerCase() === roleCode.toLowerCase()
    );

    if (!hasExistingConfig && this.isServiceProvider(roleCode)) {
      this.setDefaultPermissionsForRole(roleCode);
    }
  }

  onRoleClosed(roleCode: string): void {
    // Don't auto-deselect when closing
  }

  onRoleCheckboxChange(roleCode: string, isChecked: boolean): void {
    this.selectedRoles[roleCode] = isChecked;

    if (!isChecked) {
      // Reset to defaults when unchecking
      const isFullAccess = this.isDoctorOrNurse(roleCode);

      this.roleConfigs[roleCode] = {
        roleName: roleCode,
        specialty: undefined,
        hasFullAccess: isFullAccess,
        permissions: {
          allowedServiceRequestCategories: [],
          allowedObservationCategories: [],
          customPermissions: {
            canViewAllPatients: false,
            canOrderTests: false,
            canPrescribeMedications: false,
            canPerformProcedures: false
          }
        }
      };
      this.serviceRequestSelection[roleCode] = {};
      this.observationSelection[roleCode] = {};
    } else {
      // Doctor/Nurse: Always full access
      if (this.isDoctorOrNurse(roleCode)) {
        this.roleConfigs[roleCode].hasFullAccess = true;
        return;
      }

      // ServiceProvider: Only set defaults if it's a NEW role
      const hasExistingConfig = this.data.existingConfigurations?.some(
        config => config.roleName.toLowerCase() === roleCode.toLowerCase()
      );

      if (!hasExistingConfig && this.isServiceProvider(roleCode)) {
        this.setDefaultPermissionsForRole(roleCode);
      }
    }
  }

  onSpecialtyInput(event: Event, roleCode: string): void {
    const input = (event.target as HTMLInputElement).value.toLowerCase();

    if (!input) {
      this.filteredSpecialties[roleCode] = [...this.practitionerSpecialties];
    } else {
      this.filteredSpecialties[roleCode] = this.practitionerSpecialties.filter(specialty =>
        specialty.name.toLowerCase().includes(input)
      );
    }
  }

  displaySpecialty(value: string): string {
    return value;
  }

  onAccessLevelChange(roleCode: string): void {
    if (this.roleConfigs[roleCode].hasFullAccess) {
      // Clear permissions when switching to full access
      this.roleConfigs[roleCode].permissions = {
        allowedServiceRequestCategories: [],
        allowedObservationCategories: [],
        customPermissions: {
          canViewAllPatients: false,
          canOrderTests: false,
          canPrescribeMedications: false,
          canPerformProcedures: false
        }
      };
      this.serviceRequestSelection[roleCode] = {};
      this.observationSelection[roleCode] = {};
    } else {
      // When switching to custom permissions, only set defaults for NEW roles
      const hasExistingConfig = this.data.existingConfigurations?.some(
        config => config.roleName.toLowerCase() === roleCode.toLowerCase()
      );

      if (!hasExistingConfig) {
        // This is a new role, set defaults
        this.setDefaultPermissionsForRole(roleCode);
      }
    }
  }

  updateServiceRequestCategories(roleCode: string): void {
    const selected = Object.entries(this.serviceRequestSelection[roleCode])
      .filter(([_, isSelected]) => isSelected)
      .map(([code, _]) => code);

    this.roleConfigs[roleCode].permissions.allowedServiceRequestCategories = selected;
  }

  updateObservationCategories(roleCode: string): void {
    const selected = Object.entries(this.observationSelection[roleCode])
      .filter(([_, isSelected]) => isSelected)
      .map(([code, _]) => code);

    this.roleConfigs[roleCode].permissions.allowedObservationCategories = selected;
  }

  getSelectedCount(): number {
    return Object.values(this.selectedRoles).filter(val => val === true).length;
  }

  hasValidationErrors(): boolean {
    for (const [roleCode, isSelected] of Object.entries(this.selectedRoles)) {
      if (!isSelected) continue;

      if (!this.requiresConfiguration(roleCode)) continue;

      const config = this.roleConfigs[roleCode];

      if (this.requiresSpecialty(roleCode) && !config.specialty?.trim()) {
        return true;
      }

      // Doctor and Nurse always have full access - no validation needed
      if (this.isDoctorOrNurse(roleCode)) {
        continue;
      }

      // Only validate ServiceProvider with custom permissions
      if (this.isServiceProvider(roleCode) && !config.hasFullAccess) {
        const hasServicePerms = config.permissions.allowedServiceRequestCategories.length > 0;
        const hasObsPerms = config.permissions.allowedObservationCategories.length > 0;
        const hasCustomPerms = Object.values(config.permissions.customPermissions).some(v => v === true);

        if (!hasServicePerms && !hasObsPerms && !hasCustomPerms) {
          return true;
        }
      }
    }

    return false;
  }

  canSave(): boolean {
    return this.getSelectedCount() > 0 && !this.hasValidationErrors();
  }

  /**
   * Get default categories for role
   * Doctors and Nurses get full access by default
   * Service Providers get custom permissions
   */
  private getDefaultCategoriesForRole(roleCode: string): {
    serviceRequestCategories: string[];
    observationCategories: string[];
    canViewAllPatients: boolean;
    canOrderTests: boolean;
    canPrescribeMedications: boolean;
    canPerformProcedures: boolean;
  } {
    const lowerRole = roleCode.toLowerCase();

    // Doctors and Nurses get full access - no custom permissions needed
    if (lowerRole === 'doctor' || lowerRole === 'nurse') {
      return {
        serviceRequestCategories: [],
        observationCategories: [],
        canViewAllPatients: false,
        canOrderTests: false,
        canPrescribeMedications: false,
        canPerformProcedures: false
      };
    }

    // Service providers can have custom permissions
    // Return empty defaults - will be configured in dialog
    return {
      serviceRequestCategories: [],
      observationCategories: [],
      canViewAllPatients: false,
      canOrderTests: false,
      canPrescribeMedications: false,
      canPerformProcedures: false
    };
  }

  private setDefaultPermissionsForRole(roleCode: string): void {
    const lowerRole = roleCode.toLowerCase();

    // Doctors and Nurses always have full access - skip setting defaults
    if (this.isDoctorOrNurse(lowerRole)) {
      this.roleConfigs[roleCode].hasFullAccess = true;
      return;
    }

    // Only service providers need default permissions
    if (!this.isServiceProvider(lowerRole)) {
      return;
    }

    // Check if this role already has existing configuration loaded
    const hasExistingConfig = this.data.existingConfigurations?.some(
      config => config.roleName.toLowerCase() === lowerRole
    );

    // If editing existing config, DON'T override
    if (hasExistingConfig) {
      console.log(`⏭️ Skipping defaults for ${roleCode} - editing existing config`);
      return;
    }

    // Check if categories are loaded
    if (this.serviceRequestCategories.length === 0 || this.observationCategories.length === 0) {
      console.warn('⚠️ Categories not loaded yet, will auto-check when available');
      return;
    }

    const defaultCategories = this.getDefaultCategoriesForRole(lowerRole);

    console.log(`🎯 Setting default permissions for NEW ${roleCode}:`, defaultCategories);

    // For service providers with empty defaults, they will configure manually
    this.roleConfigs[roleCode].permissions.customPermissions = {
      canViewAllPatients: defaultCategories.canViewAllPatients,
      canOrderTests: defaultCategories.canOrderTests,
      canPrescribeMedications: defaultCategories.canPrescribeMedications,
      canPerformProcedures: defaultCategories.canPerformProcedures
    };
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.canSave()) return;

    const configurations: RoleConfiguration[] = [];

    for (const [roleCode, isSelected] of Object.entries(this.selectedRoles)) {
      if (isSelected) {
        configurations.push(this.roleConfigs[roleCode]);
      }
    }

    console.log('💾 Saving configurations:', configurations);

    this.dialogRef.close(configurations);
  }
}
